import { map } from 'rxjs/operators';

import { queryKeys } from '../../lib/queryKeys';
import type { RequestError } from '../../shared';
import { observableToPromise } from '../../shared/observableToPromise';
import { fetchGeneralAccountInfo } from '../api';
import type {
  Account,
  GeneralAccountInfoResponse,
  GeneralAccountInfoResponseRaw,
  GeneralAccountSection,
} from '../model';

import { useAccountQuery } from './useAccountQuery';

/**
 * Normalize lambda response to UI shape.
 * Backend serves zendesk flat; pass through with eap, routeRateLimits, integrations, supportEntitlement.
 */
function normalizeGeneralResponse(raw: GeneralAccountInfoResponseRaw): GeneralAccountInfoResponse {
  const eap = raw?.launchdarkly?.account?.eapSegments;
  return {
    ...(eap !== undefined && eap !== null && { eap: [...eap] }),
    ...(raw?.zendesk !== undefined && raw?.zendesk !== null && { zendesk: raw.zendesk }),
    ...(raw?.launchdarkly?.routeRateLimits !== undefined &&
      raw?.launchdarkly?.routeRateLimits !== null && {
        routeRateLimits: raw.launchdarkly.routeRateLimits,
      }),
    ...(raw?.integrations !== undefined &&
      raw?.integrations !== null && {
        integrations: [...raw.integrations],
      }),
    ...(raw?.supportEntitlement !== undefined && { supportEntitlement: raw.supportEntitlement ?? null }),
    ...(raw?.ratelimit !== undefined && raw?.ratelimit !== null && { ratelimit: raw.ratelimit }),
    ...(raw?.dataExport !== undefined && { dataExport: raw.dataExport ?? null }),
    ...(raw?.resourceLimits !== undefined &&
      raw?.resourceLimits !== null && { resourceLimits: [...raw.resourceLimits] }),
    ...(raw?.rateLimitIncreases !== undefined &&
      raw?.rateLimitIncreases !== null && { rateLimitIncreases: [...raw.rateLimitIncreases] }),
    ...(raw?.frequentContacts !== undefined && { frequentContacts: raw.frequentContacts ?? null }),
  };
}

export function useGeneralAccountInfo(account: Account | null | undefined) {
  return useAccountQuery<GeneralAccountInfoResponse, RequestError>(
    account
      ? ([...queryKeys.accounts.detail(account._id), 'general-account-info'] as readonly unknown[])
      : ['general-account-info', null],
    ({ signal }) =>
      account
        ? observableToPromise(
            fetchGeneralAccountInfo(account).pipe(
              map((res) => normalizeGeneralResponse(res as GeneralAccountInfoResponseRaw)),
            ),
            signal,
          )
        : Promise.resolve({}),
    account,
  );
}

/**
 * Fetches a single get-general section (TSS-1181) as its own query, so each
 * General-tab section loads and fails independently — a slow or failing section
 * (e.g. a warehouse-backed one) can't block or take down the others. `enabled`
 * lets a section be gated behind a feature flag. Returns the normalized response
 * containing only that section's keys.
 */
export function useGeneralAccountSection(
  account: Account | null | undefined,
  section: GeneralAccountSection,
  enabled = true,
) {
  return useAccountQuery<GeneralAccountInfoResponse, RequestError>(
    account
      ? ([...queryKeys.accounts.detail(account._id), 'general-account-info', section] as readonly unknown[])
      : ['general-account-info', section, null],
    ({ signal }) =>
      account
        ? observableToPromise(
            fetchGeneralAccountInfo(account, [section]).pipe(
              map((res) => normalizeGeneralResponse(res as GeneralAccountInfoResponseRaw)),
            ),
            signal,
          )
        : Promise.resolve({}),
    account,
    { enabled },
  );
}
